import {
  Body1,
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Spinner,
  Tooltip,
} from '@fluentui/react-components';
import {
  ArrowClockwise20Filled,
  ArrowClockwise20Regular,
  Delete20Filled,
  Delete20Regular,
  Dismiss20Filled,
  Dismiss20Regular,
  Edit20Filled,
  Edit20Regular,
  History20Filled,
  History20Regular,
  MoreHorizontal20Filled,
  MoreHorizontal20Regular,
  Play20Filled,
  Play20Regular,
  Save20Filled,
  Save20Regular,
  bundleIcon,
} from '@fluentui/react-icons';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useHttpClient } from '../../../api/HttpClientContext.js';
import { ConfirmDeleteDialog } from '../../../components/ConfirmDeleteDialog/ConfirmDeleteDialog.js';
import { VersionsRail } from '../../../components/VersionsRail/VersionsRail.js';
import { VersionStatusBadge } from '../../../components/VersionStatusBadge/index.js';
import { useCanEditResource } from '../../../hooks/useCanEditResource/useCanEditResource.js';
import { useEvaluationRunHistory } from '../../../hooks/useEvaluationRunHistory/useEvaluationRunHistory.js';
import { useTemplateVersions } from '../../../hooks/useTemplateVersions/useTemplateVersions.js';
import { EvaluationRunsService } from '../../../services/evaluationRunsService.js';
import { EvaluationTemplatesService } from '../../../services/evaluationTemplatesService.js';
import { useScenarioFactory } from '../../../telemetry/hooks/useScenarioFactory/useScenarioFactory.js';
import { useUserBi } from '../../../telemetry/hooks/useUserBi/useUserBi.js';
import { ScenarioName } from '../../../telemetry/scenarios.js';
import {
  EntityType,
  OperationType,
  PanelType,
} from '../../../telemetry/userbi.js';
import type {
  EvaluationTemplate,
  EvaluationTemplateVersion,
} from '../../../types/evaluationTemplate.js';
import { EvaluateJobsRail } from '../../AgentsPage/AgentDetailView/AgentEvaluateTab/EvaluateJobsRail.js';
import {
  EvaluationTemplateForm,
  type EvaluationTemplateWithConfig,
  type TemplateEditControls,
} from '../EvaluationTemplateForm/EvaluationTemplateForm.js';

import { useStyles } from './EvaluationTemplateDetailView.style.js';
import { JobResultDialog } from './JobResultDialog.js';
import { QueueRunDialog } from './QueueRunDialog.js';
import { TemplateVersionSnapshotDialog } from './TemplateVersionSnapshotDialog.js';
import { useTemplateRunVariables } from './useTemplateRunVariables.js';

/* eslint-disable @typescript-eslint/no-unsafe-assignment */
const PlayIcon = bundleIcon(Play20Filled, Play20Regular);
const RerunIcon = bundleIcon(ArrowClockwise20Filled, ArrowClockwise20Regular);
const EditIcon = bundleIcon(Edit20Filled, Edit20Regular);
const DeleteIcon = bundleIcon(Delete20Filled, Delete20Regular);
const DismissIcon = bundleIcon(Dismiss20Filled, Dismiss20Regular);
const HistoryIcon = bundleIcon(History20Filled, History20Regular);
const MoreIcon = bundleIcon(MoreHorizontal20Filled, MoreHorizontal20Regular);
const SaveIcon = bundleIcon(Save20Filled, Save20Regular);
/* eslint-enable @typescript-eslint/no-unsafe-assignment */

const QUEUE_REQUIRES_SNAPSHOT_DATA = {
  reason: 'snapshot_required',
  template_state: 'draft',
  snapshot_available: false,
  user_can_snapshot: false,
} as const;

export interface EvaluationTemplateDetailViewProps {
  workspaceName: string;
  templateName: string;
  /**
   * Called after the template is deleted so the parent can drop it from the
   * list and reselect. Required — the view can't meaningfully recover from a
   * delete on its own (its selection lives in the parent).
   */
  onDeleted: (name: string) => void;
  /** Called after edit or snapshot so the parent can refresh its list row. */
  onChanged?: () => void;
}

export function EvaluationTemplateDetailView({
  workspaceName,
  templateName,
  onDeleted,
  onChanged,
}: EvaluationTemplateDetailViewProps) {
  const styles = useStyles();
  const httpClient = useHttpClient();
  const scenarioFactory = useScenarioFactory();
  const { trackOperation } = useUserBi();

  const templatesService = useMemo(
    () => new EvaluationTemplatesService(httpClient),
    [httpClient],
  );
  const runsService = useMemo(
    () => new EvaluationRunsService(httpClient),
    [httpClient],
  );
  const runs = useEvaluationRunHistory({
    workspaceName,
    resourceName: templateName,
    runsService,
  });
  const { prependRun, refresh: refreshRuns } = runs;

  const [template, setTemplate] = useState<EvaluationTemplateWithConfig | null>(
    null,
  );
  const [templateLoading, setTemplateLoading] = useState(true);
  const [templateError, setTemplateError] = useState<string | null>(null);

  const [formMode, setFormMode] = useState<'view' | 'edit'>('view');
  const [editControls, setEditControls] = useState<TemplateEditControls | null>(
    null,
  );

  const [queueing, setQueueing] = useState(false);
  // The per-run parameters dialog shown before a hosted run is queued.
  const [queueDialogOpen, setQueueDialogOpen] = useState(false);
  const [snapshotting, setSnapshotting] = useState(false);
  const [snapshotVersion, setSnapshotVersion] = useState<number | null>(null);

  // Version history is fetched immediately because its latest row powers the
  // title badge; the rail itself remains hidden until requested.
  const [showVersions, setShowVersions] = useState(false);
  // The snapshot version whose read-only preview dialog is open (null = closed).
  const [viewedVersion, setViewedVersion] =
    useState<EvaluationTemplateVersion | null>(null);

  // Delete-confirmation flow — parent-owned mutation, mirroring the Agents /
  // Graders / Judge Factory pages: a loading state + inline error, and the
  // template is removed only after the DELETE succeeds.
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [actionError, setActionError] = useState<string | null>(null);

  // The completed run whose scorecard popup is open (null = closed).
  const [resultRunId, setResultRunId] = useState<string | null>(null);

  const {
    versions,
    isLoading: versionsLoading,
    error: versionsError,
    refresh: refreshVersions,
  } = useTemplateVersions({
    workspaceName,
    templateName: template?.name ?? null,
  });

  // Collapse the version panel whenever the selected template changes so a
  // re-selection never carries over the previous template's expanded state.
  useEffect(() => {
    setShowVersions(false);
    setViewedVersion(null);
    setSnapshotVersion(null);
  }, [templateName]);

  useEffect(() => {
    if (templateName.length === 0) return;
    let cancelled = false;
    setTemplateLoading(true);
    setTemplateError(null);
    setActionError(null);
    setFormMode('view');
    templatesService
      .get(workspaceName, templateName)
      .then((t) => {
        if (!cancelled) setTemplate(t as EvaluationTemplateWithConfig);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setTemplate(null);
        setTemplateError(
          err instanceof Error ? err.message : 'Failed to load template.',
        );
      })
      .finally(() => {
        if (!cancelled) setTemplateLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceName, templateName, templatesService]);

  // Edit/delete gating — see canEditResource. Queueing a run is not editing, so
  // it stays available to everyone; but the draft-snapshot step inside queueing
  // IS a mutation, so it must be gated (see handleQueue).
  const canEdit = useCanEditResource({
    workspaceName,
    resourceCreatedBy: template?.systemProperties?.createdBy,
  });

  const handleQueue = useCallback(
    async (variables?: Record<string, unknown>) => {
      if (template === null) return;
      setActionError(null);
      setQueueing(true);
      const scenario = scenarioFactory.newScenario(
        ScenarioName.EvaluationTemplateQueue,
      );
      try {
        // Hosted runs resolve the template's latest *snapshot*, never the working
        // copy. If the template has unsnapshotted edits (isDraft), snapshot first
        // so the run evaluates the current config instead of a stale version.
        if (template.isDraft) {
          if (canEdit) {
            const snapshottedName = template.name;
            const snapshot = await templatesService.snapshot(
              workspaceName,
              snapshottedName,
            );
            scenario.mark('template_snapshotted');
            // Guard by name: if the user selected a different template while the
            // snapshot was in flight, don't clear the newly-loaded template's
            // draft flag.
            setTemplate((prev) =>
              prev !== null && prev.name === snapshottedName
                ? {
                    ...prev,
                    isDraft: false,
                  }
                : prev,
            );
            setSnapshotVersion(snapshot.version);
            refreshVersions();
            onChanged?.();
          } else if (versions.length === 0 && snapshotVersion === null) {
            // Snapshotting is a gated mutation. A non-editor can still queue an
            // existing snapshot, but this draft has none yet, so there is nothing
            // runnable — surface a clear message instead of a raw server error.
            setActionError(
              'This template has unsnapshotted changes and no snapshot to run. ' +
                'Ask a workspace or template owner to snapshot it before queueing.',
            );
            scenario.complete('incomplete', QUEUE_REQUIRES_SNAPSHOT_DATA);
            return;
          }
          // Otherwise (non-editor with an existing snapshot) fall through and
          // queue the latest snapshot; the unsnapshotted edits are not included.
        }
        const run = await runsService.queue(workspaceName, {
          runOrigin: 'hosted',
          templateName: template.name,
          displayName: template.name,
          ...(variables !== undefined && Object.keys(variables).length > 0
            ? { variables }
            : {}),
        });
        scenario.mark('run_queued');
        scenario.complete('success');

        prependRun(run);
        refreshRuns();
      } catch (err) {
        scenario.complete('failure');
        setActionError(
          err instanceof Error ? err.message : 'Failed to queue run.',
        );
      } finally {
        setQueueing(false);
      }
    },
    [
      workspaceName,
      template,
      canEdit,
      versions,
      snapshotVersion,
      runsService,
      templatesService,
      prependRun,
      refreshRuns,
      refreshVersions,
      onChanged,
      scenarioFactory,
    ],
  );

  const handleSnapshot = useCallback(async () => {
    if (template === null) return;
    setActionError(null);
    setSnapshotting(true);
    try {
      const snapshot = await trackOperation(
        {
          panelType: PanelType.EvaluationTemplatesPage,
          entityType: EntityType.EvaluationTemplate,
          operation: OperationType.Snapshot,
          data: { 'Usage.Trigger': 'explicit' },
        },
        () => templatesService.snapshot(workspaceName, template.name),
      );
      setTemplate((prev) =>
        prev !== null && prev.name === template.name
          ? {
              ...prev,
              isDraft: false,
            }
          : prev,
      );
      setSnapshotVersion(snapshot.version);
      refreshVersions();
      onChanged?.();
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Failed to snapshot template.',
      );
    } finally {
      setSnapshotting(false);
    }
  }, [
    template,
    templatesService,
    workspaceName,
    refreshVersions,
    onChanged,
    trackOperation,
  ]);

  // Edit-mode callbacks — the embedded form owns the PATCH; we just
  // accept the resulting template and flip back to view mode.
  const enterEditMode = useCallback(() => {
    setFormMode('edit');
  }, []);

  const handleEditSaved = useCallback(
    (updated: EvaluationTemplate) => {
      setTemplate(
        (prev) =>
          ({
            ...(prev ?? {}),
            ...updated,
          }) as EvaluationTemplateWithConfig,
      );
      setSnapshotVersion(null);
      setFormMode('view');
      onChanged?.();
    },
    [onChanged],
  );

  const handleEditCancelled = useCallback(() => {
    setFormMode('view');
  }, []);

  const handleDelete = useCallback(async () => {
    if (template === null) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await trackOperation(
        {
          panelType: PanelType.EvaluationTemplatesPage,
          entityType: EntityType.EvaluationTemplate,
          operation: OperationType.Delete,
        },
        () => templatesService.delete(workspaceName, template.name),
      );
      setDeleteOpen(false);
      onDeleted(template.name);
    } catch (err) {
      setDeleteError(
        err instanceof Error ? err.message : 'Failed to delete template.',
      );
    } finally {
      setDeleting(false);
    }
  }, [template, templatesService, workspaceName, onDeleted, trackOperation]);

  // Switching template rows must not blank the whole pane — the title bar
  // and Jobs rail stay mounted, and only the form column swaps to a spinner
  // (or error) while the newly-selected template loads. `ready` gates the
  // header actions that need a loaded template.
  const ready = !templateLoading && templateError === null && template !== null;

  // Resolve the overridable variables up front (as soon as a template is
  // loaded) so the Queue button can decide whether a parameters dialog is even
  // needed. Templates with no overridable variables queue in one click.
  const {
    variables: runVariables,
    isLoading: runVariablesLoading,
    error: runVariablesError,
    resolved: runVariablesResolved,
  } = useTemplateRunVariables(workspaceName, template, ready);
  const hasRunVariables = runVariables.length > 0;

  // The embedded form owns unsaved edits, so queue only from read-only mode
  // where the loaded template matches the persisted working copy.
  const queueDisabled =
    queueing || snapshotting || formMode === 'edit' || !ready;
  const historyVersion = versions[versions.length - 1]?.version;
  const latestVersion =
    snapshotVersion === null
      ? historyVersion
      : Math.max(snapshotVersion, historyVersion ?? 0);
  const nextVersion = latestVersion === undefined ? null : latestVersion + 1;

  // Queuing a draft template snapshots it first (editors only). The dialog
  // surfaces this in its confirm label and an inline note.
  const willSnapshotOnQueue = ready && template.isDraft && canEdit;
  const queueConfirmLabel = willSnapshotOnQueue
    ? nextVersion === null
      ? 'Snapshot & queue'
      : `Snapshot v${String(nextVersion)} & queue`
    : 'Queue job';
  const queueDialogNote = willSnapshotOnQueue
    ? `This template has unsnapshotted edits — queuing will snapshot ${
        nextVersion === null ? 'a new version' : `v${String(nextVersion)}`
      } first, then run it.`
    : null;

  // The dialog owns the parameters step; confirming closes it and queues with
  // any per-run variable overrides. Errors surface in the Jobs rail as before.
  const handleQueueConfirm = useCallback(
    (variables: Record<string, unknown>) => {
      setQueueDialogOpen(false);
      void handleQueue(variables);
    },
    [handleQueue],
  );

  // Skip the parameters dialog entirely when there is nothing to override —
  // queue (snapshotting first if needed) in a single click. Take the direct
  // path only once resolution has actually completed for this template
  // (`runVariablesResolved`), so the hook's initial `[]/false` state on the
  // first render after a template with references loads can't bypass the
  // dialog. Any other state (still resolving, unresolved, or has variables)
  // opens the dialog.
  const canSkipQueueDialog =
    runVariablesResolved &&
    !runVariablesLoading &&
    runVariablesError === null &&
    !hasRunVariables;
  const handleQueueClick = useCallback(() => {
    if (canSkipQueueDialog) {
      void handleQueue();
    } else {
      setQueueDialogOpen(true);
    }
  }, [canSkipQueueDialog, handleQueue]);

  return (
    <div className={styles.root}>
      {/* Title bar — stays put across row switches; name falls back to the
          selected template name while the detail is still loading. */}
      <div className={styles.editorHeader}>
        <div className={styles.editorTitleRow}>
          <span className={styles.editorName}>
            {template?.name ?? templateName}
          </span>
          {ready && (
            <VersionStatusBadge
              isDraft={template.isDraft}
              version={latestVersion}
            />
          )}
        </div>
        <div className={styles.editorActions}>
          {ready && formMode === 'view' && canEdit && template.isDraft && (
            <Button
              appearance="secondary"
              icon={<SaveIcon />}
              onClick={() => void handleSnapshot()}
              disabled={snapshotting || queueing}
            >
              {snapshotting
                ? 'Snapshotting…'
                : nextVersion === null
                  ? 'Create snapshot'
                  : `Snapshot as v${String(nextVersion)}`}
            </Button>
          )}
          {ready && formMode === 'view' && canEdit && (
            <Button
              appearance="secondary"
              icon={<EditIcon />}
              onClick={enterEditMode}
            >
              Edit
            </Button>
          )}
          {ready && formMode === 'view' && canEdit && (
            <Button
              appearance="secondary"
              icon={<DeleteIcon />}
              onClick={() => setDeleteOpen(true)}
            >
              Delete
            </Button>
          )}
          {ready && formMode === 'edit' && (
            <>
              <Button
                appearance="subtle"
                icon={<DismissIcon />}
                onClick={handleEditCancelled}
                disabled={editControls?.saving ?? false}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                icon={<SaveIcon />}
                onClick={() => editControls?.save()}
                disabled={!editControls?.canSave}
              >
                {editControls?.saving ? 'Saving…' : 'Save'}
              </Button>
            </>
          )}
          {formMode === 'view' && (
            <Button
              appearance="primary"
              icon={<PlayIcon />}
              onClick={handleQueueClick}
              disabled={queueDisabled}
            >
              {queueing
                ? willSnapshotOnQueue
                  ? 'Snapshotting & queueing…'
                  : 'Queueing…'
                : 'Queue job'}
            </Button>
          )}
          {formMode === 'view' && (
            <Menu>
              <MenuTrigger disableButtonEnhancement>
                <Tooltip content="More actions" relationship="label">
                  <MenuButton
                    appearance="secondary"
                    icon={<MoreIcon />}
                    aria-label="More actions"
                  />
                </Tooltip>
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  {ready && (
                    <MenuItem
                      icon={<HistoryIcon />}
                      onClick={() => setShowVersions((prev) => !prev)}
                    >
                      {showVersions
                        ? 'Hide version history'
                        : 'Version history'}
                    </MenuItem>
                  )}
                  <MenuItem icon={<RerunIcon />} disabled>
                    Re-run last (not yet available)
                  </MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>
          )}
        </div>
      </div>

      <div className={styles.body}>
        <div className={styles.formColumn}>
          {templateLoading ? (
            <div className={styles.spinnerWrap}>
              <Spinner label="Loading template..." />
            </div>
          ) : templateError !== null || template === null ? (
            <Body1 className={styles.errorText}>
              {templateError ?? 'Template not found.'}
            </Body1>
          ) : (
            <EvaluationTemplateForm
              key={`${template.name}:${formMode}`}
              mode={formMode}
              initialTemplate={template}
              onEditSaved={handleEditSaved}
              onEditControlsChange={setEditControls}
            />
          )}
        </div>

        <EvaluateJobsRail
          runHistory={runs.runHistory}
          loading={runs.loading}
          error={runs.error}
          headerError={actionError}
          cancellingId={runs.cancellingId}
          onCancelRun={runs.cancelRun}
          onOpenResult={setResultRunId}
          emptyMessage={
            <>
              No previous jobs for this template. Click <em>Queue job</em> in
              the title bar to start one.
            </>
          }
        />

        {showVersions && (
          <VersionsRail
            versions={versions}
            loading={versionsLoading}
            error={versionsError}
            onHide={() => setShowVersions(false)}
            onViewVersion={setViewedVersion}
          />
        )}
      </div>

      {ready && (
        <QueueRunDialog
          open={queueDialogOpen}
          variables={runVariables}
          variablesLoading={runVariablesLoading}
          variablesError={runVariablesError}
          queueing={queueing}
          note={queueDialogNote}
          confirmLabel={queueConfirmLabel}
          onOpenChange={setQueueDialogOpen}
          onConfirm={handleQueueConfirm}
        />
      )}

      <TemplateVersionSnapshotDialog
        version={viewedVersion}
        onClose={() => setViewedVersion(null)}
      />

      <JobResultDialog
        workspaceName={workspaceName}
        runId={resultRunId}
        runsService={runsService}
        onClose={() => setResultRunId(null)}
      />

      <ConfirmDeleteDialog
        open={deleteOpen}
        title="Delete eval template"
        deleting={deleting}
        error={deleteError}
        onOpenChange={(open) => {
          setDeleteOpen(open);
          if (!open) setDeleteError(null);
        }}
        onConfirm={() => void handleDelete()}
      >
        Delete <strong>{template?.name ?? templateName}</strong>? This action
        cannot be undone.
      </ConfirmDeleteDialog>
    </div>
  );
}
